import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Button,
  Link,
  Text,
  useBreakpoint,
  useTheme
} from "@seneca/senekit";

import { fetchAllClassSkeletonsIfNotFetched } from "seneca-common/features/class/service";
import { isSignedIn } from "seneca-common/features/user/state";
import useLocationOnMount from "seneca-common/utils/hooks/routing/useLocationOnMount";
import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";
import useEffectWhenPropBecomesTrue from "seneca-common/utils/hooks/useEffectWhenPropBecomesTrue";

import { teacherPreviewKey } from "features/classroom/routes/classroom/routes/course/routes/section/routes/session/links";
import TextPlaceholder from "features/common/components/TextPlaceholder";
import { BooksEmoji } from "features/common/emoji";
import { LastLocation } from "features/common/routing/with-last-location";
import { linkToTeacherDashboard } from "features/teachers/links";
import { isInTeacherPlatform } from "features/teachers/routes/links";
import { isSchoolTeacher } from "features/user/selectors";

import useGetTeacherTopbarProps from "../../hooks/useGetTeacherTopbarProps";
import Topbar from "../Topbar";
import styles from "./TeacherTopbar.module.scss";

type Props = {
  courseId?: string;
  backToTeacherPlatform?: LastLocation;
};

export default function TeacherTopbar({
  courseId,
  backToTeacherPlatform
}: Props) {
  const theme = useTheme();
  const { isMobileOnly } = useBreakpoint();
  const { loading, hasNoClasses, creatingClass, createMyFirstClass } =
    useGetTeacherTopbarProps(courseId);

  const dispatch = useDispatch();
  const display = useSelector(isSchoolTeacher);
  const signedIn = useSelector(isSignedIn);

  useEffectOnMount(() => {
    display && dispatch(fetchAllClassSkeletonsIfNotFetched());
  });
  useEffectWhenPropBecomesTrue(
    () => dispatch(fetchAllClassSkeletonsIfNotFetched()),
    display
  );
  useEffectWhenPropBecomesTrue(
    () => dispatch(fetchAllClassSkeletonsIfNotFetched()),
    signedIn
  );
  const location = useLocationOnMount();

  if (!display) {
    return null;
  }

  if (loading) {
    return (
      <Topbar>
        <TextPlaceholder
          loading={loading}
          placeholderWidth="30%"
          loadingStateDelay={0}
        />
      </Topbar>
    );
  }

  if (hasNoClasses && courseId) {
    return (
      <Topbar>
        <Box display={["none", "block"]}>
          <Text>
            Create a class for this course to monitor your student's progress
          </Text>
        </Box>

        <Box marginLeft={["none", "xlarge"]}>
          <Button
            onClick={() => createMyFirstClass && createMyFirstClass()}
            loading={creatingClass}
          >
            Create a class {isMobileOnly && " for this course"}
          </Button>
        </Box>
      </Topbar>
    );
  }

  const goBackToSpecificTeacherPage =
    backToTeacherPlatform &&
    isInTeacherPlatform(backToTeacherPlatform.pathname);

  const linkProps = goBackToSpecificTeacherPage
    ? {
        to: backToTeacherPlatform.pathname,
        state: backToTeacherPlatform.state
      }
    : {
        to: linkToTeacherDashboard()
      };

  const isOnPreview = new URLSearchParams(location.search).get(
    teacherPreviewKey
  );
  const topBarText = isOnPreview
    ? "Back to assignment creation"
    : "Back to teacher platform";

  return (
    <Topbar>
      <BooksEmoji
        className={classnames(styles.emoji, styles.hideEmojiMobile)}
      />
      <Link {...linkProps}>
        <Box color={theme.white}>{topBarText}</Box>
      </Link>
    </Topbar>
  );
}
